import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, useMediaQuery
} from '@mui/material';
import {
  Search, Add, Edit, Visibility, Delete, Refresh, CardGiftcard
} from '@mui/icons-material';
import SchemeModal from './SchemeModal';
import Pagination from '../../common/Pagination';

const SchemeIndex = () => {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, token, userId } = useAuth();
  const { logAudit } = useAuditLog();
  const isMobile = useMediaQuery('(max-width:768px)');

  // Modal states (desktop)
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [formData, setFormData] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});
  const [schemeDetails, setSchemeDetails] = useState([]);
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  // Data states
  const [schemes, setSchemes] = useState([]);

  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Loading & messages
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch
  const fetchSchemes = async () => {
    try {
      const endpoint = `/${companyId}/${branchId}/${fromDate}/${toDate}`;
      const response = await fetchData('SchemeApi', endpoint, token);
      let data = [];
      if (response) {
        if (response.response_data) data = Array.isArray(response.response_data) ? response.response_data : [];
        else if (Array.isArray(response)) data = response;
        else if (response.data) data = Array.isArray(response.data) ? response.data : [];
      }
      setSchemes([...data]);
    } catch (e) {
      console.error('Error fetching schemes:', e);
      setSchemes([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (e) { setItems([]); }
  };

  const fetchBrands = async () => {
    try {
      const response = await fetchData('BrandApi', `/${companyId}`, token);
      setBrands(response.response_data || response || []);
    } catch (e) { setBrands([]); }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchData('CategoryApi', `/${companyId}`, token);
      setCategories(response.response_data || response || []);
    } catch (e) { setCategories([]); }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching schemes...');
    await Promise.allSettled([fetchSchemes(), fetchItems(), fetchBrands(), fetchCategories()]);
    setLoading(false);
  };

  const handleRefresh = () => fetchAllData();

  // Smart handlers: Mobile → full page, Desktop → modal
  const handleCreate = () => {
    if (isMobile) { history.push('/SchemeForm/create'); }
    else { openCreateModal(); }
  };
  const handleView = (id) => {
    if (isMobile) { history.push(`/SchemeForm/view/${id}`); }
    else { openEditViewModal('view', id); }
  };
  const handleEdit = (id) => {
    if (isMobile) { history.push(`/SchemeForm/edit/${id}`); }
    else { openEditViewModal('edit', id); }
  };

  // Desktop modal helpers
  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('SchemeApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || data.SchemeNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      let schemeNo = '';
      if (billPrefix && finYear) schemeNo = `${billPrefix}/${billNumber}/${finYear}`;
      else if (finYear) schemeNo = `${billNumber}/${finYear}`;
      else if (billPrefix) schemeNo = `${billPrefix}/${billNumber}`;
      else schemeNo = `${billNumber}`;
      return { billNumber, billPrefix, finYear, schemeNo };
    } catch (e) { return { billNumber: 1, billPrefix: '', finYear: '', schemeNo: '1' }; }
  };

  const openCreateModal = async () => {
    setLoading(true);
    const maxBillData = await fetchMaxBillNo();
    setFormData({
      CompanyId: companyId, BranchId: branchId || 1, MasterId: null,
      SchemeNumber: maxBillData.billNumber, SchemeNo: maxBillData.schemeNo,
      FinYear: maxBillData.finYear,
      SchemeStartDate: new Date().toISOString().split('T')[0],
      SchemeEndDate: '',
      SupplierName: '', ContactNumber: '', Address: '', Remarks: '',
      IsActive: true
    });
    setSchemeDetails([]);
    setModalAction('create');
    setOpenModal(true);
    setLoading(false);
  };

  const openEditViewModal = async (action, id) => {
    setLoading(true);
    try {
      const response = await fetchData('SchemeApi', `/${id}`, token);
      const freshData = response.response_data || response;
      const schemeData = freshData.Scheme || freshData;
      const details = freshData.SchemeDetails || freshData.Details || [];
      const fd = {
        Id: schemeData.Id, CompanyId: schemeData.CompanyId || companyId,
        BranchId: schemeData.BranchId || branchId, MasterId: schemeData.MasterId || null,
        SchemeNumber: schemeData.SchemeNumber || 0, SchemeNo: schemeData.SchemeNo || '',
        FinYear: schemeData.FinYear || '',
        SchemeStartDate: schemeData.SchemeStartDate?.split('T')[0] || '',
        SchemeEndDate: schemeData.SchemeEndDate?.split('T')[0] || '',
        SupplierName: schemeData.SupplierName || '', ContactNumber: schemeData.ContactNumber || '',
        Address: schemeData.Address || '', Remarks: schemeData.Remarks || '',
        IsActive: schemeData.IsActive ?? true,
        // Preserve audit fields for update
        CreatedBy: schemeData.CreatedBy ?? null,
        CreatedDate: schemeData.CreatedDate ?? null,
      };
      setFormData(fd);
      setOriginalFormData(fd);
      setSchemeDetails(details.map(d => ({
        Id: d.Id || 0, BrandId: d.BrandId || null, CategoryId: d.CategoryId || null,
        ItemId: d.ItemId || null, IsTarget: d.IsTarget || 1,
        Quantity: d.Quantity || 0, Rate: d.Rate || 0,
        Description: d.Description || '', IsActive: d.IsActive ?? true,
        // Preserve audit fields for update
        CreatedBy: d.CreatedBy ?? null,
        CreatedDate: d.CreatedDate ?? null,
        // NotMapped display fields from API
        ItemName: d.ItemName || null,
        BrandName: d.BrandName || null,
        CategoryName: d.CategoryName || null,
      })));
      setModalAction(action);
      if (action === 'view') {
        logAudit(AUDIT_ACTION.VIEW, 'SchemeApi', schemeData.Id || id || 0);
      }
      setOpenModal(true);
    } catch (e) { setError('Error loading scheme'); }
    setLoading(false);
  };

  const handleCloseModal = () => { setOpenModal(false); setFormData({}); setSchemeDetails([]); };

  // Save (desktop modal)
  const handleSave = async () => {
    if (!formData.SupplierName?.trim()) { setError('Supplier Name is required'); return; }
    if (schemeDetails.length === 0) { setError('Please add at least one scheme detail'); return; }
    // Validate: remove blank rows (no item selected) and check qty/rate
    const validDetails = schemeDetails.filter(d => d.ItemId);
    if (validDetails.length === 0) { setError('Please select at least one item in scheme details'); return; }
    const invalidQtyRow = validDetails.find(d => d.IsTarget === 1 && (!d.Quantity || parseFloat(d.Quantity) <= 0));
    if (invalidQtyRow) { setError('Quantity is required for all items with Target type "Qty"'); return; }
    const invalidRateRow = validDetails.find(d => d.IsTarget === 2 && (!d.Rate || parseFloat(d.Rate) <= 0));
    if (invalidRateRow) { setError('Rate is required for all items with Target type "Rate"'); return; }
    setSchemeDetails(validDetails);
    setLoading(true);
    // Local datetime string: "2026-07-16T10:30:00.000"
    const now = new Date();
    const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, -1);
    try {
      const payload = {
        Scheme: { ...formData, CompanyId: companyId, BranchId: branchId || 1,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy, ModifiedBy: userId,
          CreatedDate: modalAction === 'create' ? localNow : formData.CreatedDate,
          ModifiedDate: localNow
        },
        SchemeDetails: schemeDetails.map(d => ({ ...d, CompanyId: companyId, BranchId: branchId || 1,
          SchemeId: formData.Id || 0,
          Quantity: d.IsTarget === 1 ? (parseFloat(d.Quantity) || 0) : 0,
          Rate: d.IsTarget === 2 ? (parseFloat(d.Rate) || 0) : 0,
          CreatedBy: modalAction === 'create' ? userId : (d.CreatedBy ?? null),
          CreatedDate: modalAction === 'create' ? localNow : (d.CreatedDate ?? null),
          ModifiedBy: userId, ModifiedDate: localNow,
          // strip NotMapped display fields — not needed in payload
          ItemName: undefined, BrandName: undefined, CategoryName: undefined,
        }))
      };
      if (modalAction === 'create') { await postData('SchemeApi', '', payload, token); setSuccess('Scheme created!');
        logAudit(AUDIT_ACTION.CREATE, 'SchemeApi', 0, null, { SchemeNo: formData.SchemeNo, SupplierName: formData.SupplierName || '' });
      }
      else { await updateData('SchemeApi', formData.Id, payload, token); setSuccess('Scheme updated!');
        logAudit(AUDIT_ACTION.UPDATE, 'SchemeApi', formData.Id, { ...originalFormData }, { ...formData });
      }
      await fetchSchemes();
      handleCloseModal();
    } catch (e) { setError(e.response?.data?.message || 'Error saving scheme'); }
    finally { setLoading(false); }
  };

  // Delete
  const handleDeleteClick = (id, no) => { setDeleteItemId(id); setDeleteItemNo(no); setDeleteModalOpen(true); };
  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await deleteData('SchemeApi', deleteItemId, token);
      setSuccess('Scheme deleted!');
      logAudit(AUDIT_ACTION.DELETE, 'SchemeApi', deleteItemId, { SchemeNo: deleteItemNo }, null);
      setDeleteModalOpen(false);
      fetchSchemes();
    } catch (e) { setError('Error deleting scheme'); }
    finally { setLoading(false); }
  };

  // Filter & Pagination
  const filteredSchemes = schemes.filter(item => {
    const s = searchTerm.toLowerCase();
    return (item.SchemeNo || '').toLowerCase().includes(s) ||
      (item.SupplierName || '').toLowerCase().includes(s) ||
      (item.ContactNumber || '').toLowerCase().includes(s) ||
      (item.Remarks || '').toLowerCase().includes(s);
  });
  const totalPages = Math.ceil(filteredSchemes.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredSchemes.slice(startIndex, startIndex + rowsPerPage);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading} message={loadingMessage} />

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Scheme Management</h4>
        </div>

        {/* Search, Filters and Create */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="New Scheme" arrow>
                <IconButton onClick={handleCreate} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                  <Add sx={{ fontSize: '18px' }} />
                </IconButton>
              </Tooltip>
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Scheme No, Supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Scheme No, Supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="New Scheme" arrow>
              <Button variant="contained" onClick={handleCreate}
                sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>New Scheme</span>
              </Button>
            </Tooltip>
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Scheme No.', 'Start Date', 'End Date', 'Supplier', 'Contact', 'Remarks', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 8 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <CardGiftcard sx={{ fontSize: 16, color: '#2C3E6E' }} />
                          {item.SchemeNo || `${item.SchemeNumber || ''}`}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.SchemeStartDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.SchemeEndDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.SupplierName || '-'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.ContactNumber || '-'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.Remarks || '-'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        {item.IsActive ? <Chip label="Active" size="small" color="success" /> : <Chip label="Inactive" size="small" color="default" />}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <Tooltip title="View" arrow>
                            <IconButton size="small" onClick={() => handleView(item.Id)} sx={{ color: '#2C3E6E', border: '1px solid #e2e8f0', borderRadius: '6px', width: 26, height: 26 }}>
                              <Visibility sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" onClick={() => handleEdit(item.Id)} sx={{ color: '#059669', border: '1px solid #e2e8f0', borderRadius: '6px', width: 26, height: 26 }}>
                              <Edit sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton size="small" onClick={() => handleDeleteClick(item.Id, item.SchemeNo || item.SchemeNumber)} sx={{ color: '#dc2626', border: '1px solid #e2e8f0', borderRadius: '6px', width: 26, height: 26 }}>
                              <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '13px' }}>
                      No schemes found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        {filteredSchemes.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            totalItems={filteredSchemes.length}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={(val) => { setRowsPerPage(val); setCurrentPage(1); }}
          />
        )}
      </div>

      {/* Scheme Modal (Desktop only) */}
      {!isMobile && (
        <SchemeModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          schemeDetails={schemeDetails}
          setSchemeDetails={setSchemeDetails}
          onSave={handleSave}
          items={items}
          brands={brands}
          categories={categories}
          loading={loading}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {/* Delete Confirmation */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#dc2626' }}>Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete scheme <strong>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModalOpen(false)} variant="outlined" size="small">Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" size="small">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SchemeIndex;
